import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Search, Plus, ArrowRight } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { QUICK_ACTIONS } from '../components/home/homeConfig';
import useHomeData from '../components/home/useHomeData';
import Launchpad from '../components/home/Launchpad';
import KpiCards from '../components/home/KpiCards';
import ChartCard from '../components/home/ChartCard';
import RevenueArea from '../components/home/charts/RevenueArea';
import OrdersDonut from '../components/home/charts/OrdersDonut';
import ARAgingBars from '../components/home/charts/ARAgingBars';
import RecentActivityFeed from '../components/home/RecentActivityFeed';

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const openCommandPalette = () => {
  window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, ctrlKey: true, bubbles: true }));
};

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loading, kpis, revenueData, ordersByStatus, arAging, recentActivities } = useHomeData();

  const firstName = (user?.full_name || 'there').split(' ')[0];
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <Layout>
      <div className="bg-slate-50 min-h-full">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                {greeting()}, {firstName}
              </h1>
              <p className="text-sm text-slate-500 mt-1">{today}</p>
            </div>
            <div className="flex items-center gap-3 w-full md:w-auto">
              <button
                onClick={openCommandPalette}
                data-testid="home-search-trigger"
                className="flex-1 md:w-72 flex items-center gap-2 px-3 py-2.5 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-slate-500 transition-colors"
              >
                <Search className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm">Search modules, actions…</span>
                <kbd className="ml-auto text-[11px] font-medium text-slate-400 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 hidden sm:inline">⌘K</kbd>
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="bg-orange-600 hover:bg-orange-700 shrink-0" data-testid="quick-actions-dropdown">
                    <Plus className="w-4 h-4 mr-1.5" />
                    Create
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {QUICK_ACTIONS.map((qa) => {
                    const Icon = qa.icon;
                    return (
                      <DropdownMenuItem
                        key={qa.path}
                        onClick={() => navigate(qa.path)}
                        data-testid={`quick-action-${qa.path}`}
                        className="cursor-pointer"
                      >
                        <Icon className="w-4 h-4 mr-2 text-orange-600" />
                        {qa.label}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Odoo-style launchpad */}
          <Launchpad />

          {/* KPI metrics */}
          <KpiCards kpis={kpis} loading={loading} />

          {/* Analytics bento */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <ChartCard
              testid="chart-revenue"
              className="lg:col-span-2"
              title="Revenue Trend"
              subtitle="Sales order value, last 6 months"
              loading={loading}
            >
              <RevenueArea data={revenueData} />
            </ChartCard>

            <ChartCard
              testid="chart-orders-status"
              title="Orders by Status"
              subtitle="Distribution of all sales orders"
              loading={loading}
              isEmpty={!loading && ordersByStatus.length === 0}
            >
              <OrdersDonut data={ordersByStatus} />
            </ChartCard>

            <ChartCard
              testid="chart-recent-activity"
              className="lg:col-span-2"
              title="Recent Activity"
              subtitle="Latest documents across modules"
              loading={loading}
              isEmpty={!loading && recentActivities.length === 0}
              emptyText="No recent activity"
              action={
                <Button variant="ghost" size="sm" className="text-orange-600 hover:text-orange-700" onClick={() => navigate('/otc/sales-orders')}>
                  View all <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              }
            >
              <RecentActivityFeed activities={recentActivities} />
            </ChartCard>

            <ChartCard
              testid="chart-ar-aging"
              title="AR Aging"
              subtitle="Outstanding receivables by age"
              loading={loading}
              isEmpty={!loading && arAging.every((b) => b.amount === 0)}
              emptyText="No open receivables"
            >
              <ARAgingBars data={arAging} />
            </ChartCard>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HomePage;
